import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { css, Popper, styled } from '@mui/material';
import {
  Badge,
  IconButtonGhost,
  InfoButton,
  Spinner,
  Tabs,
  Tooltip,
  Typography,
} from '@kc-console/common-ui';
import {
  TOKEN_SCOPE,
  useNotificationFeed,
  useReadAllNotifications,
  useReadNotification,
  useServiceAppState,
  useStreamControl,
} from '@kc-console/context';
import { useClickOutside, useEscapeKey, useTranslation } from '@kc-console/core';
import {
  IcCloseLargeRegular,
  IcDoubleCheckRegular,
  IcRefreshRegular,
  IcSettingType1Regular,
  IllustStatusBlank,
} from '@kep/kcds-icons';

import { GNB_HEIGHT } from '../../constants/layout';
import { useOpenAnnouncementDetailModal } from '../../hooks/useOpenAnnouncementDetailModal';
import NotificationItemCard from './NotificationItemCard';
import NotificationSettingPopper from './NotificationSettingPopper';

import type { MouseEvent } from 'react';
import type { PopperPlacementType } from '@mui/material';
import type { Notification, NotificationTabKey } from '@kc-console/context';

interface NotificationPopperProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  open: boolean;
  placement?: PopperPlacementType;
}

// 끝에서 4번째(한 페이지 10개 기준 7번째) 카드가 보이면 다음 페이지를 미리 요청.
const PREFETCH_FROM_END = 4;

// 종 위치와 무관하게 뷰포트 기준(우측 -32px / 상단 GNB 높이)에 고정하기 위한 가상 anchor.
const VIEWPORT_RIGHT_GAP = 32;
const VIEWPORT_BOTTOM_GAP = 32;

/**
 * GNB 알림 패널.
 * @description
 * - 닫혀 있어도 언마운트하지 않는다. feed 쿼리 구독을 유지해 SSE 수신 시점에 목록이 갱신되고,
 *   푸시를 눌러 열 때 새 알림이 이미 보이게 하기 위함이다.
 * - 대신 탭 상태가 남으므로 열릴 때마다 '전체' 탭으로 되돌린다.
 */
const NotificationPopper = ({
  anchorEl,
  onClose,
  open,
  placement = 'bottom-end',
}: NotificationPopperProps) => {
  const { t } = useTranslation();
  const {
    state: { scope },
  } = useServiceAppState();
  const betaInfoTooltipMessage = t('layout:notification.popper.beta_guide');
  const isNotProjectScope = scope !== TOKEN_SCOPE.PROJECT;

  const tabs = [
    { value: 'all', label: t('common:common.label.all') },
    {
      value: 'resource',
      label: (
        <Tooltip
          message={
            isNotProjectScope ? t('layout:notification.popper.tab.resource.disabled.tooltip') : ''
          }
          placement="bottom"
        >
          <ResourceTabTooltipTrigger>
            {t('layout:notification.popper.tab.resource.label')}
          </ResourceTabTooltipTrigger>
        </Tooltip>
      ),
      disabled: isNotProjectScope,
    },
    { value: 'platform_notice', label: t('layout:notification.popper.tab.platform_notice.label') },
  ];

  const [tab, setTab] = useState<NotificationTabKey>('all');
  const [settingOpen, setSettingOpen] = useState(false);
  const [settingAnchorEl, setSettingAnchorEl] = useState<HTMLElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // 컴포넌트가 닫혀도 유지되어 마지막 탭이 남는다. 항상 최신순 '전체' 탭으로 열리도록 되돌린다.
  useEffect(() => {
    if (open) setTab('all');
  }, [open]);

  const { mutate: readAll } = useReadAllNotifications();
  const { mutate: read } = useReadNotification();
  const { connect, isConnectionLost } = useStreamControl();
  const openAnnouncementDetailModal = useOpenAnnouncementDetailModal();

  const { visible, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, refetch } =
    useNotificationFeed({
      tab,
    });

  const triggerIndex = Math.max(0, visible.length - PREFETCH_FROM_END);

  // 트리거 카드에 연결되는 콜백 ref — 목록이 늘어나면 대상 노드가 바뀌며 자동 재관찰.
  const triggerRef = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      if (!node || !hasNextPage || isFetchingNextPage) return;
      observerRef.current = new IntersectionObserver(entries => {
        if (entries[0]?.isIntersecting) fetchNextPage();
      });
      observerRef.current.observe(node);
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  const handleRead = useCallback(
    (notification: Notification) => {
      if (notification.read || !notification.id) return;
      read(notification.id);
    },
    [read],
  );

  // 다른 GNB 메뉴(DropdownMenu2)와 UX 통일 — 외부 클릭/Escape 로 패널 닫기.
  // DropdownMenu2 와 동일하게, anchorEl 에 toggleId 를 부여해 종(anchor) 클릭은
  // 토글 핸들러가 처리하도록 click-outside 에서 무시한다.
  const anchorElHtmlElement =
    typeof document !== 'undefined' && anchorEl instanceof HTMLElement ? anchorEl : null;
  const anchorToggleFallbackId = useId();

  const handleClose = useCallback(() => {
    setSettingOpen(false);
    onClose();
  }, [onClose]);

  const handleViewNotice = useCallback(
    (notification: Notification) => {
      if (!notification.resourceId) return;

      handleRead(notification);
      handleClose();
      openAnnouncementDetailModal({ initialAnnouncementId: notification.resourceId });
    },
    [handleClose, handleRead, openAnnouncementDetailModal],
  );

  const panelRef = useClickOutside<HTMLDivElement>({
    enabled: open,
    onClickOutside: handleClose,
    ignoreSelectors: anchorElHtmlElement
      ? [`[data-toggle-id="${anchorElHtmlElement.dataset.toggleId || anchorToggleFallbackId}"]`]
      : [],
  });

  const handleSettingClick = useCallback((event: MouseEvent<HTMLElement>) => {
    setSettingAnchorEl(event.currentTarget);
    setSettingOpen(prev => !prev);
  }, []);

  const handleRestartStream = useCallback(() => {
    connect();
    refetch();
  }, [connect, refetch]);

  useEscapeKey({ enabled: open, onEscape: handleClose });

  useEffect(() => {
    if (anchorElHtmlElement && !anchorElHtmlElement.dataset.toggleId) {
      anchorElHtmlElement.dataset.toggleId = anchorToggleFallbackId;
      return () => {
        delete anchorElHtmlElement.dataset.toggleId;
      };
    }
  }, [anchorElHtmlElement, anchorToggleFallbackId]);

  // 로드된 목록에 안 읽은 알림이 1개라도 있으면 '모두 읽음' 버튼을 활성화한다.
  const hasUnread = useMemo(() => visible.some(n => !n.read), [visible]);

  const virtualAnchorEl = useMemo(() => {
    if (!anchorEl) return null;
    return {
      getBoundingClientRect: () => {
        const right = window.innerWidth - VIEWPORT_RIGHT_GAP;
        const top = GNB_HEIGHT;

        return {
          width: 0,
          height: 0,
          top,
          bottom: top,
          left: right,
          right,
          x: right,
          y: top,
        } as DOMRect;
      },
    };
  }, [anchorEl]);

  return (
    <Popper
      anchorEl={virtualAnchorEl}
      modifiers={[{ name: 'offset', options: { offset: [0, 0] } }]}
      open={open}
      placement={placement}
      style={{ zIndex: 1300 }}
    >
      <Root ref={panelRef}>
        <Header>
          <HeaderText>
            <HeaderTitle>
              <Typography variant="label/semibold/xl">
                {t('layout:modal.title.notification')}
              </Typography>
              <Badge color="purple" variant="ghost" shape="rectangular" label="Beta" />
              <InfoButton
                alertType="tooltip"
                arrow="down"
                size="sm"
                tooltipProps={{ message: betaInfoTooltipMessage }}
              />
            </HeaderTitle>
            <Typography variant="label/regular/sm" color="text/weakest/default">
              {t('layout:notification.popper.description')}
            </Typography>
          </HeaderText>
          <HeaderActions>
            <IconButtonGhost
              variant="primary"
              size="sm"
              onClick={handleSettingClick}
              aria-label={t('layout:notification.popper.setting.label')}
            >
              <IcSettingType1Regular />
            </IconButtonGhost>
            <IconButtonGhost
              variant="primary"
              size="lg"
              onClick={handleClose}
              aria-label={t('common:common.action.close')}
            >
              <IcCloseLargeRegular />
            </IconButtonGhost>
          </HeaderActions>
        </Header>

        <TabBar>
          <Tabs
            size="md"
            value={tab}
            onChange={value => setTab(value as NotificationTabKey)}
            items={tabs}
          />
          <ReadAllButton
            type="button"
            onClick={() => readAll()}
            disabled={!hasUnread}
            data-disabled={!hasUnread}
          >
            <IcDoubleCheckRegular width={16} height={16} />
            <Typography
              variant={hasUnread ? 'body/normal/sm' : 'label/medium/xs'}
              color={hasUnread ? 'text/interactive/default' : 'text/disabled'}
            >
              {t('layout:notification.popper.mark_all_read.label')}
            </Typography>
          </ReadAllButton>
        </TabBar>

        {isConnectionLost && (
          <StreamDisconnectedMessage>
            <Typography variant="label/regular/sm" color="text/weaker/default">
              {t('layout:notification.popper.disconnected.message')}
            </Typography>
            <RestartStreamButton type="button" onClick={handleRestartStream}>
              <Typography variant="label/regular/sm" color="text/interactive/default">
                {t('layout:notification.popper.disconnected.refresh')}
              </Typography>
              <IcRefreshRegular width={12} height={12} />
            </RestartStreamButton>
            <Typography variant="label/regular/sm" color="text/weaker/default">
              {t('layout:notification.popper.disconnected.reconnect_guide')}
            </Typography>
          </StreamDisconnectedMessage>
        )}

        <Body key={tab}>
          {isLoading ? (
            <LoadingState>
              <Spinner size="lg" />
            </LoadingState>
          ) : visible.length === 0 ? (
            <EmptyState>
              <EmptyIllust>
                <IllustStatusBlank />
              </EmptyIllust>
              <Typography variant="label/medium/lg" color="text/weakest/default">
                {t('layout:notification.popper.empty.description')}
              </Typography>
            </EmptyState>
          ) : (
            <List>
              {visible.map((notification, index) => (
                <NotificationItemCard
                  key={notification.id}
                  ref={index === triggerIndex ? triggerRef : undefined}
                  notification={notification}
                  onClose={handleClose}
                  onRead={handleRead}
                  onViewNotice={handleViewNotice}
                />
              ))}
            </List>
          )}
        </Body>
      </Root>
      <NotificationSettingPopper
        anchorEl={settingAnchorEl}
        notificationToggleId={anchorElHtmlElement?.dataset.toggleId || anchorToggleFallbackId}
        open={open && settingOpen}
      />
    </Popper>
  );
};

export default NotificationPopper;

// Figma 스펙: Width 520 / 기본 Height 760 / radius borderRadius/4(4px) / padding spacing/24 / gap spacing/8.
// 뷰포트가 작으면 GNB와 하단 안전 여백을 제외한 높이까지 줄어든다.
const Root = styled('div')(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    width: 520px;
    height: 760px;
    max-height: calc(100vh - ${GNB_HEIGHT}px - ${VIEWPORT_BOTTOM_GAP}px);
    /* Body가 전체 폭을 차지해 스크롤바는 파퍼 우측 끝에 붙고, 고정 영역과 목록이 24px 정렬선을 관리한다. */
    padding: 24px 0 0;
    background-color: ${theme.color['surface/default']};
    border: ${theme.mixins.border('border/weak/default')};
    border-radius: 4px;
    box-shadow: ${theme.shadows[8]};
    overflow: hidden;
  `,
);

const Header = styled('div')`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin: 0 24px 20px;
`;

const HeaderText = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 0;
  min-width: 0;
`;

const HeaderTitle = styled('div')`
  display: flex;
  align-items: center;
  gap: 0;
`;

const HeaderActions = styled('div')`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const TabBar = styled('div')(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 0 24px;
    position: relative;

    /* Tabs 내부 하단 라인은 탭 영역까지만 그어지므로, '모두 읽음' 쪽까지 이어지도록
     ::before로 TabBar 전체 폭에 같은 라인을 깐다.
     ::before는 자식(Tabs)보다 먼저 그려져 선택된 탭 배경이 라인을 가린다(= 탭 아래는 끊김). */
    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 1px;
      border-bottom: ${theme.mixins.border('border/weak/default', 1)};
    }
  `,
);

const ReadAllButton = styled('button')(
  ({ theme }) => css`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    padding: 4px 0;
    background: none;
    border: none;
    cursor: pointer;
    color: ${theme.color['text/interactive/default']};

    &:focus-visible {
      outline: 2px solid ${theme.color['border/select']};
      outline-offset: 2px;
      border-radius: 2px;
    }

    /* 모두 읽음 처리된 상태 — 아이콘 색 icon/disabled, 텍스트는 inline에서 text/disabled. */
    &:disabled {
      cursor: default;
      color: ${theme.color['icon/disabled']};
    }
  `,
);

const StreamDisconnectedMessage = styled('div')(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    flex-shrink: 0;
    margin: 8px 24px 0;
    height: 48px;
    background-color: ${theme.color['surface/neutral/weakest/default']};
  `,
);

const RestartStreamButton = styled('button')(
  ({ theme }) => css`
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 0;
    color: ${theme.color['text/interactive/default']};
    background: none;
    border: none;
    cursor: pointer;
  `,
);

const Body = styled('div')`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
`;

const List = styled('div')`
  padding: 0 24px;
`;

const EmptyState = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 100%;
`;

/** SVG 자체에 width/height 어트리뷰트가 없어 CSS로 크기를 강제. */
const EmptyIllust = styled('div')`
  width: 72px;
  height: 72px;

  & > svg {
    width: 100%;
    height: 100%;
  }
`;

const LoadingState = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100%;
`;

const ResourceTabTooltipTrigger = styled('span')`
  display: inline-flex;
  pointer-events: auto;
`;
